import React, { useRef, useState } from "react";

import { useTranslation } from "utils";

import { COVER_CROP_MIX_TYPE, CoverCropMixType, HelpFieldKey, SOIL_SIGHT_DRAWER_WIDTH } from "consts";

import { SoilSightCoverCropDefaults, SoilSightCropDefaults, SoilSightCropDefaultsByCropId, SoilSightNonCropDefaults } from "models";

import { Button, Drawer, Form, Option, Select, Typography } from "components";

import { GuidancePanel } from "pages/app/scenario-analysis/components/GuidancePanel";
import { HelpFieldWrapper } from "pages/app/scenario-analysis/components/HelpFieldWrapper";
import { CoverCropSection } from "pages/app/scenario-analysis/components/crop-details-input-context-drawer/CoverCropSection";
import { CropDetailsEconomicsFields } from "pages/app/scenario-analysis/components/crop-details-input-context-drawer/CropDetailsEconomicsFields";
import { PracticesSection } from "pages/app/scenario-analysis/components/crop-details-input-context-drawer/PracticesSection";
import { useCropDetailsGuidance } from "pages/app/scenario-analysis/hooks/useCropDetailsGuidance";
import { useCropDetailsInitialValues } from "pages/app/scenario-analysis/hooks/useCropDetailsInitialValues";
import { CropYear, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import { InheritedCoverCropDefaultsByKey, getCropChangeUpdates, withoutUntouchedPrefills } from "pages/app/scenario-analysis/utils/cropDetailsDefaults";
import { getResidueLockState, growsCoverCrop, isResidueLockedToLeftOnField } from "pages/app/scenario-analysis/utils/cropYearHelpers";

export interface CropDetailsDrawerProps {
  visible: boolean;
  onClose: () => void;
  yearIndex: number;
  cropYear: CropYear;
  mainCropOptions: ScenarioMainCropOption[];
  mainCropLabelLookup: Record<string, string>;
  onSaveForYear: (cropYear: CropYear) => void;
  onApplyToAll: (cropYear: CropYear) => void;

  cropDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  /** Per-crop settings inherited from the most-recently-refined year with the same cropId across all scenarios. Takes priority over API defaults for initial population. */
  inheritedDefaultsByCropId?: SoilSightCropDefaultsByCropId;
  /** Per crop+coverCropId settings inherited from the most-recently-refined year with the same combination. Key format: `${cropId}::${coverCropId}`. Takes priority over API cover crop defaults. */
  inheritedCoverCropDefaultsByKey?: InheritedCoverCropDefaultsByKey;

  coverCropDefaults?: SoilSightNonCropDefaults["coverCropDefaults"] | null;
}

export const CropDetailsDrawer = ({
  visible,
  onClose,
  yearIndex,
  cropYear,
  cropDefaultsByCropId,
  inheritedDefaultsByCropId,
  inheritedCoverCropDefaultsByKey,
  coverCropDefaults,
  mainCropOptions,
  mainCropLabelLookup,
  onSaveForYear,
  onApplyToAll,
}: CropDetailsDrawerProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const [form] = Form.useForm();
  const [hasErrors, setHasErrors] = useState(false);
  const { focusedField, focusedGuidance, guidancePanelRef, guidance } = useCropDetailsGuidance();
  const { initialFormValues, initialEconomicsSnapshot, getEffectiveDefaults, getEffectiveCoverCropDefaults } = useCropDetailsInitialValues({
    cropYear,
    mainCropOptions,
    cropDefaultsByCropId,
    inheritedDefaultsByCropId,
    inheritedCoverCropDefaultsByKey,
    coverCropDefaults,
  });

  const mainCropValue: string = Form.useWatch("crop", form) ?? cropYear.crop;
  const coverCropIdValue: string | undefined = Form.useWatch("coverCropId", form) ?? cropYear.coverCropId ?? undefined;

  const hasCoverCrop = growsCoverCrop({ coverCropId: coverCropIdValue });
  const cropLabel = mainCropLabelLookup[mainCropValue] ?? mainCropValue;
  const isResidueDisabledForCrop = getResidueLockState(mainCropOptions, mainCropValue).disabled;
  const showResidueLockInfo = isResidueLockedToLeftOnField(mainCropOptions, mainCropValue);

  // Defaults the previous crop filled in, so a crop change only moves fields the user didn't edit.
  const previousCropDefaultsRef = useRef<SoilSightCropDefaults | undefined>(initialEconomicsSnapshot);

  // Cover crop inputs' mount values: follow a type change, never the typing.
  const [coverCropInitialValues, setCoverCropInitialValues] = useState<SoilSightCoverCropDefaults>({
    legumeShare: initialFormValues.legumeShare,
    ccCost: initialFormValues.ccCost,
  });

  // Migrates fields still on the previous crop's default; user-overridden values are left alone.
  const propagateCropDefaults = (newCropId: string) => {
    const nextDefaults = getEffectiveDefaults(newCropId);
    const updates = getCropChangeUpdates((field) => form.getFieldValue(field) as number | null | undefined, previousCropDefaultsRef.current, nextDefaults);
    if (Object.keys(updates).length > 0) form.setFieldsValue(updates);
    previousCropDefaultsRef.current = nextDefaults;
  };

  // Cover crop values belong to the type, so a type change always applies the new type's defaults.
  const propagateCoverCropDefaults = (mixType: CoverCropMixType) => {
    if (mixType === COVER_CROP_MIX_TYPE.NONE) {
      form.setFieldsValue({
        legumeShare: null,
        ccCost: null,
      });
      setCoverCropInitialValues({ legumeShare: null, ccCost: null });
    } else {
      const nextDefaults = getEffectiveCoverCropDefaults(mainCropValue, mixType);
      const legumeShare = nextDefaults?.legumeShare ?? null;
      const ccCost = nextDefaults?.ccCost ?? null;
      form.setFieldsValue({ legumeShare, ccCost });
      setCoverCropInitialValues({ legumeShare, ccCost });
    }
  };

  const handleValuesChange = (changedValues: Record<string, unknown>) => {
    const changedField = Object.keys(changedValues)[0] as HelpFieldKey;
    if (changedField) guidance.onFocus(changedField);

    if ("fodderLosses" in changedValues) {
      form.validateFields(["mouldLosses"]).catch(() => {});
    }
    if ("crop" in changedValues) {
      const newCropId = changedValues.crop as string;
      propagateCropDefaults(newCropId);
      const { lockedValue } = getResidueLockState(mainCropOptions, newCropId);
      if (lockedValue !== undefined) {
        form.setFieldValue("residue", lockedValue);
      }
    }
    if ("coverCropId" in changedValues) {
      propagateCoverCropDefaults(changedValues.coverCropId as CoverCropMixType);
    }
  };

  // Runs the action only on a valid form; validation errors are shown inline.
  const whenValid = (action: () => void) => async () => {
    const isValid = await form.validateFields().then(
      () => true,
      () => false
    );
    if (isValid) action();
  };

  const handleSave = whenValid(() => {
    const values = form.getFieldsValue(true) as Partial<CropYear>;
    const openedWith: Partial<CropYear> = { ...initialEconomicsSnapshot, legumeShare: initialFormValues.legumeShare, ccCost: initialFormValues.ccCost };
    onSaveForYear({ ...cropYear, ...withoutUntouchedPrefills(values, cropYear, openedWith) });
  });

  // Unlike save, apply-to-all keeps prefills so every matching year ends up as shown.
  const handleApplyToAll = whenValid(() => {
    const values = form.getFieldsValue(true);
    onApplyToAll({ ...cropYear, ...values });
  });

  const footerButtons = [
    <Button key="save" type="secondary" dataTestId="crop-details-save" disabled={hasErrors} onClick={handleSave}>
      {t("cropDetailsDrawer.saveForYear")}
    </Button>,
    <Button key="apply" type="primary" dataTestId="crop-details-apply-all" disabled={hasErrors} onClick={handleApplyToAll}>
      {t("cropDetailsDrawer.applyToAll")} {cropLabel}
    </Button>,
  ];

  const drawerTitle = (
    <div className="crop-details-drawer-header">
      <div className="crop-details-drawer-title-main">{t("cropDetailsDrawer.refineCropDetails")}</div>
      <div className="crop-details-drawer-title-sub">
        {t("cropDetailsDrawer.year")} {yearIndex}
      </div>
    </div>
  );

  return (
    <Drawer
      className="crop-details-drawer"
      visible={visible}
      title={drawerTitle}
      footer={footerButtons}
      onClose={onClose}
      destroyOnHidden
      dataTestId="crop-details-drawer"
      width={SOIL_SIGHT_DRAWER_WIDTH}
    >
      <div className="crop-details-drawer-body">
        {/* ── Left: input form ── */}
        <Form
          form={form}
          className="crop-details-drawer-left"
          initialValues={initialFormValues}
          onValuesChange={handleValuesChange}
          onFieldsChange={(_, allFields) => {
            setHasErrors(allFields.some((field) => (field.errors?.length ?? 0) > 0));
          }}
        >
          {/* Crop section */}
          <section className="crop-details-drawer-section-card">
            <div className="crop-details-drawer-section-card-heading">{guidance.getFieldLabel("crop")}</div>
            <HelpFieldWrapper fieldKey="crop" onFocus={guidance.onFocus}>
              <Select name="crop" label={guidance.getFieldLabel("crop")} initialValue={initialFormValues.crop ?? undefined} showSearch>
                {mainCropOptions.map((o) => (
                  <Option key={o.value} value={o.value}>
                    {o.label}
                  </Option>
                ))}
              </Select>
            </HelpFieldWrapper>
          </section>

          <CropDetailsEconomicsFields form={form} initialValues={initialFormValues} guidance={guidance} />

          <PracticesSection
            focusedField={focusedField}
            guidance={guidance}
            isResidueDisabled={isResidueDisabledForCrop}
            showResidueLockInfo={showResidueLockInfo}
          />

          <CoverCropSection
            initialCoverCropId={initialFormValues.coverCropId}
            hasCoverCrop={hasCoverCrop}
            coverCropInitialValues={coverCropInitialValues}
            guidance={guidance}
          />
        </Form>

        {/* ── Right: guidance panel ── */}
        <aside ref={guidancePanelRef} className="crop-details-drawer-right">
          {focusedGuidance ? (
            <GuidancePanel
              key={focusedField}
              fieldLabel={focusedGuidance.fieldLabel}
              description={focusedGuidance.description}
              source={focusedGuidance.source}
              whatIsItLabel={t("cropDetailsDrawer.guidance.whatIsThis")}
              sourceLabel={t("cropDetailsDrawer.guidance.source")}
              unit={focusedGuidance.unit}
            />
          ) : (
            <Typography variant="paragraph-s" className="crop-details-drawer-guidance-prompt">
              {t("cropDetailsDrawer.guidance.defaultPrompt")}
            </Typography>
          )}
        </aside>
      </div>
    </Drawer>
  );
};

export default CropDetailsDrawer;
